import { ChevronRight } from 'lucide-react';
import React from 'react';
import { useCasePartnerShell } from '@/components/contexts/CasePartnerContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { CrimsonMarkIcon } from '@/components/ui/icons';
import { cn } from '@/lib/utils';

const CASE_PARTNER_TAB_ID = 'case-partner';

export const GlobalSidePanel: React.FC = () => {
  const { isOpen, tabs, activeTab, closePanel, setActiveTab } = useSidePanel();
  const casePartnerShell = useCasePartnerShell();

  if (!isOpen) return null;

  const validTabs = tabs.filter(tab => tab.content !== null && tab.content !== undefined);
  // Filter out disabled tabs from display (don't show them at all instead of graying them out)
  const displayTabs = validTabs.filter(tab => !tab.disabled);
  const standardTabs = displayTabs.filter(tab => tab.id !== CASE_PARTNER_TAB_ID);
  const casePartnerTab = displayTabs.find(tab => tab.id === CASE_PARTNER_TAB_ID);
  const finalActiveTab = validTabs.find(t => t.id === activeTab)?.id || validTabs[0]?.id;
  const isCasePartnerTabActive = finalActiveTab === CASE_PARTNER_TAB_ID;

  return (
    <div
      data-side-panel
      className="h-full flex flex-col bg-white border-l border-slate-200 overflow-hidden"
    >
      {/* Tab Header */}
      <div className="shrink-0 border-b border-slate-200 bg-white/95">
        <div className="flex h-12 items-center px-4">
          {/* Tab Pills */}
          {standardTabs.length > 1 ? (
            <div className="flex min-w-0 items-center gap-1.5">
              {standardTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = tab.id === finalActiveTab;

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'relative h-8 rounded-md px-2.5 text-xs font-medium transition-colors',
                      'after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full after:transition-opacity',
                      isActive
                        ? 'text-slate-950 after:bg-slate-500 after:opacity-100'
                        : 'text-slate-500 after:opacity-0 hover:bg-slate-50 hover:text-slate-800'
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5" />
                      <span>{tab.label}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : standardTabs.length === 1 ? (
            <div className="flex min-w-0 items-center gap-1.5">
              <button
                type="button"
                onClick={() => setActiveTab(standardTabs[0].id)}
                className={cn(
                  'relative h-8 rounded-md px-2.5 text-xs font-medium transition-colors',
                  'after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full after:transition-opacity',
                  standardTabs[0].id === finalActiveTab
                    ? 'text-slate-950 after:bg-slate-500 after:opacity-100'
                    : 'text-slate-500 after:opacity-0 hover:bg-slate-50 hover:text-slate-800'
                )}
              >
                <div className="flex items-center gap-1.5">
                  {React.createElement(standardTabs[0].icon, { className: 'h-3.5 w-3.5' })}
                  <span>{standardTabs[0].label}</span>
                </div>
              </button>
            </div>
          ) : (
            <div />
          )}

          <div className="ml-auto flex items-center gap-2.5">
            {casePartnerTab && (
              <button
                type="button"
                onClick={() => setActiveTab(casePartnerTab.id)}
                className={cn(
                  'group relative h-8 rounded-md px-2.5 text-xs font-medium transition-colors',
                  'after:absolute after:inset-x-2 after:bottom-0 after:h-0.5 after:rounded-full after:transition-opacity',
                  isCasePartnerTabActive
                    ? 'text-slate-950 after:bg-[#ac2f2f] after:opacity-100'
                    : 'text-slate-500 after:opacity-0 hover:bg-slate-50 hover:text-slate-800'
                )}
              >
                <div className="flex items-center gap-1.5">
                  <span className="relative flex h-3.5 w-3.5 shrink-0 items-center justify-center">
                    <CrimsonMarkIcon
                      className={cn(
                        'h-3.5 w-3.5 text-crimson-600 transition-opacity',
                        isCasePartnerTabActive ? 'opacity-65' : 'opacity-45 group-hover:opacity-60'
                      )}
                    />
                  </span>
                  <span className="relative">
                    {casePartnerTab.label}
                    {casePartnerShell.isStreaming && !isCasePartnerTabActive && (
                      <span
                        aria-hidden="true"
                        className="-right-2 -top-1 absolute h-2 w-2 animate-pulse rounded-full bg-primary-400"
                      />
                    )}
                  </span>
                </div>
              </button>
            )}

            {casePartnerTab && <div className="h-5 w-px bg-slate-200" />}

            {/* Collapse Button */}
            <button
              type="button"
              onClick={closePanel}
              className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-50 hover:text-slate-700"
              aria-label="Collapse panel"
              title="Collapse panel"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 min-h-0 relative">
        {validTabs.map((tab) => {
          const isActive = tab.id === finalActiveTab;

          return (
            <div
              key={tab.id}
              aria-hidden={!isActive}
              className={cn(
                'absolute inset-0 bg-white',
                isActive ? 'z-10' : 'z-0'
              )}
              style={{
                display: isActive ? 'block' : 'none',
                pointerEvents: isActive ? 'auto' : 'none',
              }}
            >
              {tab.content}
            </div>
          );
        })}
      </div>
    </div>
  );
};
